import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Select, 
  DatePicker, 
  message, 
  Tag, 
  Space,
  Card,
  Modal,
  Descriptions,
  Dropdown
} from 'antd';
import { 
  ReloadOutlined,
  EyeOutlined,
  DownOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;
const { RangePicker } = DatePicker;

const OperationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDevice, setFilterDevice] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, devicesRes] = await Promise.all([
        api.get('/logs', {
          params: {
            deviceId: filterDevice,
            type: filterType,
            startDate: dateRange ? dateRange[0].format('YYYY-MM-DD HH:mm:ss') : null,
            endDate: dateRange ? dateRange[1].format('YYYY-MM-DD HH:mm:ss') : null,
            limit: 200
          }
        }),
        api.get('/devices')
      ]);

      setLogs(logsRes.data.data || []);
      setDevices(devicesRes.data.data || []);
    } catch (error) {
      message.error('获取日志失败');
      console.error('Fetch logs error:', error);
    } finally {
      setLoading(false);
    }
  }, [filterDevice, filterType, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showDetail = (log) => {
    setSelectedLog(log);
    setDetailVisible(true);
  };

  const getTypeColor = (type) => {
    const colorMap = {
      '设备注册': 'blue',
      '设备更新': 'cyan',
      '设备删除': 'red',
      'APK上传': 'green',
      'APK删除': 'orange',
      '创建指令': 'purple',
      '安装应用': 'blue',
      '卸载应用': 'orange',
      '更新应用': 'cyan',
      '取消命令': 'gray',
      '禁用应用': 'red',
      '启用应用': 'green',
      '禁止卸载应用': 'orange',
      '允许卸载应用': 'cyan',
      '隐藏应用': 'purple',
      '显示应用': 'blue',
      '批量隐藏应用': 'purple',
      '批量显示应用': 'blue',
      '已安装应用上报': 'cyan',
      '规则复制': 'purple',
      '规则复制源': 'gray'
    };
    return colorMap[type] || 'default';
  };

  const getTypeLabel = (type) => {
    // 类型已经是中文，直接返回
    return type;
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {getTypeLabel(type)}
        </Tag>
      )
    },
    {
      title: '设备名称',
      dataIndex: 'deviceId',
      key: 'deviceId',
      width: 150,
      render: (deviceId) => {
        const device = devices.find(d => d.deviceId === deviceId);
        return device ? device.deviceName : deviceId;
      }
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'detail',
                icon: <EyeOutlined />,
                label: '详情',
                onClick: () => showDetail(record)
              }
            ]
          }}
          trigger={['hover']}
        >
          <Button>
            操作 <DownOutlined />
          </Button>
        </Dropdown>
      )
    }
  ];

  return (
    <div>
      <Card
        title="操作日志"
        extra={
          <Space>
            <Select
              placeholder="选择设备"
              style={{ width: 200 }}
              allowClear
              onChange={setFilterDevice}
              value={filterDevice}
            >
              {devices.map(device => (
                <Option key={device.deviceId} value={device.deviceId}>
                  {device.deviceName}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="选择类型"
              style={{ width: 150 }}
              allowClear
              onChange={setFilterType}
              value={filterType}
            >
              <Option value="设备注册">设备注册</Option>
              <Option value="设备更新">设备更新</Option>
              <Option value="设备删除">设备删除</Option>
              <Option value="APK上传">APK上传</Option>
              <Option value="APK删除">APK删除</Option>
              <Option value="安装应用">安装应用</Option>
              <Option value="卸载应用">卸载应用</Option>
              <Option value="更新应用">更新应用</Option>
              <Option value="取消命令">取消命令</Option>
              <Option value="禁用应用">禁用应用</Option>
              <Option value="启用应用">启用应用</Option>
              <Option value="禁止卸载应用">禁止卸载应用</Option>
              <Option value="允许卸载应用">允许卸载应用</Option>
              <Option value="隐藏应用">隐藏应用</Option>
              <Option value="显示应用">显示应用</Option>
              <Option value="批量隐藏应用">批量隐藏应用</Option>
              <Option value="批量显示应用">批量显示应用</Option>
              <Option value="已安装应用上报">已安装应用上报</Option>
              <Option value="规则复制">规则复制</Option>
              <Option value="规则复制源">规则复制源</Option>
            </Select>
            <RangePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              onChange={setDateRange}
              value={dateRange}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={logs}
          columns={columns}
          rowKey={(record) => `${record.timestamp}-${record.type}-${record.message}`}
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条日志`
          }}
        />
      </Card>

      <Modal
        title="日志详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="时间" span={2}>
              {new Date(selectedLog.timestamp).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="类型">
              <Tag color={getTypeColor(selectedLog.type)}>
                {getTypeLabel(selectedLog.type)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="设备ID">
              {selectedLog.deviceId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="消息" span={2}>
              {selectedLog.message}
            </Descriptions.Item>
            <Descriptions.Item label="详细信息" span={2}>
              <pre style={{ 
                background: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '4px',
                margin: 0,
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                {JSON.stringify(selectedLog.details || {}, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default OperationLogs;
